import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Tabs,
  Switch,
  Tag,
  Pagination,
  Space,
  message,
  Alert,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  EditOutlined,
  CloseOutlined,
  DownOutlined,
  DownloadOutlined,
  // EyeOutlined,
} from "@ant-design/icons";
import AddPincodeModal from "./AddPincodeModal";
import PincodeDetailsDrawer from "./PincodeDetailsDrawer";
import CreateZoneModal from "./CreateZoneModal";
import PincodeApi from "../../api/PincodeApi";
import "./styles.scss";

const { TabPane } = Tabs;

const PincodeManagement = () => {
  const [activeTab, setActiveTab] = useState("ecomm");
  
  // Hyperlocal state
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]); // Will be populated from API
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [pincodeFilter, setPincodeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  // Courier-specific filters
  const [zoneFilter, setZoneFilter] = useState("");
  const [stateCodeFilter, setStateCodeFilter] = useState("");
  const [districtIpFilter, setDistrictIpFilter] = useState("");
  const [pincodeTypeFilter, setPincodeTypeFilter] = useState("");
  const [serviceabilityTypeFilter, setServiceabilityTypeFilter] = useState("");
  const [newCityFilter, setNewCityFilter] = useState("");
  // Other available filters
  const [searchFilter, setSearchFilter] = useState("");
  const [dpIdFilter, setDpIdFilter] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  // Pincode metadata filters
  const [metadataPincodeFilter, setMetadataPincodeFilter] = useState("");
  const [areaNameFilter, setAreaNameFilter] = useState("");
  const [areaTypeFilter, setAreaTypeFilter] = useState("");
  const [transitDaysFilter, setTransitDaysFilter] = useState("");
  const [metadataPincodeTypeFilter, setMetadataPincodeTypeFilter] = useState("");
  
  // Debounced filter values for API calls
  const [debouncedPincodeFilter, setDebouncedPincodeFilter] = useState("");
  const [debouncedStateFilter, setDebouncedStateFilter] = useState("");
  const [debouncedCityFilter, setDebouncedCityFilter] = useState("");
  const [debouncedZoneFilter, setDebouncedZoneFilter] = useState("");
  const [debouncedStateCodeFilter, setDebouncedStateCodeFilter] = useState("");
  const [debouncedDistrictIpFilter, setDebouncedDistrictIpFilter] = useState("");
  const [debouncedPincodeTypeFilter, setDebouncedPincodeTypeFilter] = useState("");
  const [debouncedServiceabilityTypeFilter, setDebouncedServiceabilityTypeFilter] = useState("");
  const [debouncedNewCityFilter, setDebouncedNewCityFilter] = useState("");
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState("");
  const [debouncedDpIdFilter, setDebouncedDpIdFilter] = useState("");
  const [debouncedServiceTypeFilter, setDebouncedServiceTypeFilter] = useState("");
  // Debounced metadata filters
  const [debouncedMetadataPincodeFilter, setDebouncedMetadataPincodeFilter] = useState("");
  const [debouncedAreaNameFilter, setDebouncedAreaNameFilter] = useState("");
  const [debouncedAreaTypeFilter, setDebouncedAreaTypeFilter] = useState("");
  const [debouncedTransitDaysFilter, setDebouncedTransitDaysFilter] = useState("");
  const [debouncedMetadataPincodeTypeFilter, setDebouncedMetadataPincodeTypeFilter] = useState("");
  // const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isDetailsDrawerVisible, setIsDetailsDrawerVisible] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showTooltip, setShowTooltip] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [isCreateZoneModalVisible, setIsCreateZoneModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Pincode data from API
  const [pincodeData, setPincodeData] = useState([]);
  // Pincode metadata data from API
  const [metadataData, setMetadataData] = useState([]);
  
  // Ref to track if we need to force API call (when Enter is pressed)
  const forceFetchRef = React.useRef(false);

  // Fetch pincode data from API
  // Note: Backend filter behavior differs by business_line:
  // - COURIER: city/state filters search in BOTH courier_pincode_metadata AND pincode_serviceability tables
  // - ECOMM: city/state filters only search in pincode_serviceability table (unchanged)
  // - Pincode filter works the same for all business lines
  const fetchPincodeData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
        business_line: activeTab, // "ecomm" or "courier"
      };

      // Add optional filters only if they have values
      // Backend handles empty/whitespace filters by ignoring them
      if (debouncedPincodeFilter.trim()) {
        params.pincode = debouncedPincodeFilter.trim();
      }
      if (debouncedStateFilter.trim()) {
        params.state = debouncedStateFilter.trim();
      }
      if (debouncedCityFilter.trim()) {
        params.city = debouncedCityFilter.trim();
      }
      if (debouncedSearchFilter.trim()) {
        params.search = debouncedSearchFilter.trim();
      }
      if (debouncedDpIdFilter.trim()) {
        params.dp_id = debouncedDpIdFilter.trim();
      }
      if (debouncedServiceTypeFilter.trim()) {
        params.service_type = debouncedServiceTypeFilter.trim();
      }
      if (isActiveFilter !== "") {
        params.is_active = isActiveFilter;
      }
      if (debouncedZoneFilter.trim()) {
        params.zone = debouncedZoneFilter.trim();
      }
      // Courier-specific filters (only send when on courier tab)
      if (activeTab === "courier") {
        if (debouncedStateCodeFilter.trim()) {
          params.state_code = debouncedStateCodeFilter.trim();
        }
        if (debouncedDistrictIpFilter.trim()) {
          params.district_ip = debouncedDistrictIpFilter.trim();
        }
        if (debouncedPincodeTypeFilter.trim()) {
          params.pincode_type = debouncedPincodeTypeFilter.trim();
        }
        if (debouncedServiceabilityTypeFilter.trim()) {
          params.serviceability_type = debouncedServiceabilityTypeFilter.trim();
        }
        if (debouncedNewCityFilter.trim()) {
          params.new_city = debouncedNewCityFilter.trim();
        }
      }

      const response = await PincodeApi.getPincodeServiceability(params);

      // Transform API response to match component structure
      const transformedData = response.data.map((item, index) => ({
        key: `${currentPage}-${index}`,
        pincode: item.pincode,
        state: item.state,
        city: item.city,
        services: item.services || [],
        is_active: item.is_active,
        id: item.pincode,
        // Hub name (populated based on business_line: ecomm/courier/cargo)
        hub_name: item.hub_name ?? null,
        // Include new optional fields for courier business line
        zone: item.zone ?? null,
        district_ip: item.district_ip ?? null,
        state_code: item.state_code ?? null,
        pincode_type: item.pincode_type ?? null,
        serviceability_type: item.serviceability_type ?? null,
        new_city: item.new_city ?? null,
      }));

      setPincodeData(transformedData);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error("Error fetching pincode data:", error);
      message.error("Failed to fetch pincode data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedPincodeFilter,
    debouncedStateFilter,
    debouncedCityFilter,
    debouncedZoneFilter,
    debouncedStateCodeFilter,
    debouncedDistrictIpFilter,
    debouncedPincodeTypeFilter,
    debouncedServiceabilityTypeFilter,
    debouncedNewCityFilter,
    debouncedSearchFilter,
    debouncedDpIdFilter,
    debouncedServiceTypeFilter,
    isActiveFilter,
    activeTab,
  ]);

  // Debounce filter inputs (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPincodeFilter(pincodeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [pincodeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStateFilter(stateFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [stateFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCityFilter(cityFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [cityFilter]);

  // Debounce courier-specific filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoneFilter(zoneFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [zoneFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStateCodeFilter(stateCodeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [stateCodeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDistrictIpFilter(districtIpFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [districtIpFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPincodeTypeFilter(pincodeTypeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [pincodeTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedServiceabilityTypeFilter(serviceabilityTypeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [serviceabilityTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNewCityFilter(newCityFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [newCityFilter]);

  // Debounce other filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDpIdFilter(dpIdFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [dpIdFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedServiceTypeFilter(serviceTypeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [serviceTypeFilter]);

  // Debounce metadata filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMetadataPincodeFilter(metadataPincodeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [metadataPincodeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAreaNameFilter(areaNameFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [areaNameFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAreaTypeFilter(areaTypeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [areaTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTransitDaysFilter(transitDaysFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [transitDaysFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMetadataPincodeTypeFilter(metadataPincodeTypeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [metadataPincodeTypeFilter]);

  // Reset page to 1 when debounced filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedPincodeFilter,
    debouncedStateFilter,
    debouncedCityFilter,
    debouncedZoneFilter,
    debouncedStateCodeFilter,
    debouncedDistrictIpFilter,
    debouncedPincodeTypeFilter,
    debouncedServiceabilityTypeFilter,
    debouncedNewCityFilter,
    debouncedSearchFilter,
    debouncedDpIdFilter,
    debouncedServiceTypeFilter,
    isActiveFilter,
    activeTab,
    debouncedMetadataPincodeFilter,
    debouncedAreaNameFilter,
    debouncedAreaTypeFilter,
    debouncedTransitDaysFilter,
    debouncedMetadataPincodeTypeFilter,
  ]);

  // Fetch pincode metadata areas
  const fetchPincodeMetadata = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
      };

      // Add optional filters only if they have values
      if (debouncedMetadataPincodeFilter.trim()) {
        params.pincode = debouncedMetadataPincodeFilter.trim();
      }
      if (debouncedAreaNameFilter.trim()) {
        params.area_name = debouncedAreaNameFilter.trim();
      }
      if (debouncedAreaTypeFilter.trim()) {
        params.area_type = debouncedAreaTypeFilter.trim();
      }
      if (debouncedTransitDaysFilter.trim()) {
        params.transit_days_for_delivery = debouncedTransitDaysFilter.trim();
      }
      if (debouncedMetadataPincodeTypeFilter.trim()) {
        params.pincode_type = debouncedMetadataPincodeTypeFilter.trim();
      }

      const response = await PincodeApi.getPincodeAreas(params);

      // Transform API response to match component structure
      const transformedData = response.data.items.map((item, index) => ({
        key: `${currentPage}-${index}`,
        id: item.id,
        pincode: item.pincode,
        area_name: item.area_name,
        area_type: item.area_type ?? null,
        transit_days_for_delivery: item.transit_days_for_delivery ?? null,
        pincode_type: item.pincode_type ?? null,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setMetadataData(transformedData);
      setTotalRecords(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching pincode metadata:", error);
      message.error("Failed to fetch pincode metadata. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedMetadataPincodeFilter,
    debouncedAreaNameFilter,
    debouncedAreaTypeFilter,
    debouncedTransitDaysFilter,
    debouncedMetadataPincodeTypeFilter,
  ]);

  // Fetch states when hyperlocal tab is active
  useEffect(() => {
    if (activeTab === "hyperlocal") {
      fetchStates();
    }
  }, [activeTab]);

  // Fetch all states
  const fetchStates = async () => {
    try {
      setLoadingStates(true);
      const response = await PincodeApi.getAllStates();
      
      // Handle different response formats
      let stateList = [];
      if (Array.isArray(response)) {
        stateList = response;
      } else if (Array.isArray(response?.data)) {
        stateList = response.data;
      } else if (response?.data?.states && Array.isArray(response.data.states)) {
        stateList = response.data.states;
      } else if (response?.states && Array.isArray(response.states)) {
        stateList = response.states;
      }
      
      setStates(stateList);
    } catch (error) {
      console.error("Error fetching states:", error);
      message.error("Failed to fetch states. Please try again.");
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch cities by state
  const fetchCitiesByState = async (state) => {
    try {
      setLoadingCities(true);
      const response = await PincodeApi.getCitiesByState(state);
      
      console.log("Cities API Response:", response);
      
      // Handle different response formats
      let cityList = [];
      if (Array.isArray(response)) {
        cityList = response;
      } else if (response?.city && Array.isArray(response.city)) {
        // API returns { "city": [...] }
        cityList = response.city;
      } else if (Array.isArray(response?.data)) {
        cityList = response.data;
      } else if (response?.data?.city && Array.isArray(response.data.city)) {
        cityList = response.data.city;
      } else if (response?.data?.cities && Array.isArray(response.data.cities)) {
        cityList = response.data.cities;
      } else if (response?.cities && Array.isArray(response.cities)) {
        cityList = response.cities;
      }
      
      console.log("Parsed city list:", cityList);
      setCities(cityList);
    } catch (error) {
      console.error("Error fetching cities:", error);
      message.error("Failed to fetch cities. Please try again.");
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch zones by city
  const fetchZonesByCity = async (city) => {
    try {
      setLoadingZones(true);
      const response = await PincodeApi.getHyperlocalZones(city);
      
      console.log("Zones API Response:", response);
      
      // Handle response format: { "status": 200, "data": { "totalCount": 2, "items": [...] } }
      let zoneList = [];
      if (response?.data?.items && Array.isArray(response.data.items)) {
        zoneList = response.data.items.map(zone => ({
          id: zone._id,
          name: zone.name,
          isActive: zone.isActive,
          isExpanded: false,
          city: zone.city,
          state: zone.state,
          serviceablePincodes: zone.serviceablePincodes || [],
          orderProcessType: zone.orderProcessType,
          acceptNormalOrder: zone.acceptNormalOrder,
          acceptSubscriptionOrder: zone.acceptSubscriptionOrder,
        }));
      }
      
      console.log("Parsed zone list:", zoneList);
      setZones(zoneList);
    } catch (error) {
      console.error("Error fetching zones:", error);
      message.error("Failed to fetch zones. Please try again.");
      setZones([]);
    } finally {
      setLoadingZones(false);
    }
  };

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    if (activeTab === "metadata") {
      fetchPincodeMetadata();
    } else if (activeTab !== "hyperlocal") {
      fetchPincodeData();
    }
  }, [fetchPincodeData, fetchPincodeMetadata, activeTab]);
  
  // Force immediate API call when filter is manually updated (Enter pressed)
  // This ensures the API is called even if React batches the state updates
  useEffect(() => {
    if (forceFetchRef.current && activeTab !== "hyperlocal") {
      forceFetchRef.current = false;
      // Use setTimeout to ensure state updates have been applied
      setTimeout(() => {
        if (activeTab === "metadata") {
          fetchPincodeMetadata();
        } else if (activeTab !== "hyperlocal") {
          fetchPincodeData();
        }
      }, 0);
    }
  }, [
    debouncedPincodeFilter,
    debouncedStateFilter,
    debouncedCityFilter,
    debouncedZoneFilter,
    debouncedStateCodeFilter,
    debouncedDistrictIpFilter,
    debouncedPincodeTypeFilter,
    debouncedServiceabilityTypeFilter,
    debouncedNewCityFilter,
    debouncedSearchFilter,
    debouncedDpIdFilter,
    debouncedServiceTypeFilter,
    isActiveFilter,
    currentPage,
    activeTab,
    debouncedMetadataPincodeFilter,
    debouncedAreaNameFilter,
    debouncedAreaTypeFilter,
    debouncedTransitDaysFilter,
    debouncedMetadataPincodeTypeFilter,
    fetchPincodeMetadata,
  ]);

  // Auto-hide tooltip after 10 seconds
  useEffect(() => {
    if (showTooltip && activeTab === "ecomm") {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip, activeTab]);

  // Show tooltip when switching to ecomm tab
  useEffect(() => {
    if (activeTab === "ecomm") {
      setShowTooltip(true);
    }
  }, [activeTab]);

  const handleStatusToggle = async (record, checked) => {
    // Only allow toggle when edit mode is enabled
    if (!editMode) {
      return;
    }

    try {
      // Optimistically update UI
      setPincodeData((prev) =>
        prev.map((item) =>
          item.key === record.key ? { ...item, is_active: checked } : item
        )
      );

      // Call API to toggle pincode active status
      await PincodeApi.togglePincodeActive(
        [record.pincode],
        checked,
        activeTab // "ecomm" or "courier"
      );

      // Refresh data to sync with server
      fetchPincodeData();

      message.success(
        `Pincode ${record.pincode} ${
          checked ? "activated" : "deactivated"
        } successfully`
      );
    } catch (error) {
      // Revert UI change on error
      setPincodeData((prev) =>
        prev.map((item) =>
          item.key === record.key ? { ...item, is_active: !checked } : item
        )
      );
      console.error("Error updating pincode status:", error);
      message.error(
        error.message || "Failed to update pincode status. Please try again."
      );
    }
  };

  // const handleViewDetails = async (record) => {
  //   try {
  //     setLoading(true);
  //     const detailData = await PincodeApi.getPincodeServiceabilityDetail(
  //       record.pincode
  //     );
  //     setSelectedPincode({ ...record, ...detailData });
  //     setIsDetailsDrawerVisible(true);
  //   } catch (error) {
  //     console.error("Error fetching pincode details:", error);
  //     message.error("Failed to fetch pincode details. Please try again.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleAddPincode = () => {
    setModalMode("add");
    setSelectedPincode(null);
    setIsAddModalVisible(true);
  };

  const handleEditPincode = (record) => {
    setModalMode("edit");
    setSelectedPincode(record);
    setIsAddModalVisible(true);
  };

  // Hyperlocal handlers
  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedCity("");
    if (value) {
      fetchCitiesByState(value);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (value) => {
    setSelectedCity(value);
    if (value) {
      fetchZonesByCity(value);
    } else {
      setZones([]);
    }
  };

  const handleClearHyperlocalFilters = () => {
    setSelectedState("");
    setSelectedCity("");
    setCities([]);
    setZones([]);
  };

  const handleZoneToggle = async (zoneId, checked) => {
    try {
      // Find the zone to get its current data
      const zone = zones.find((z) => z.id === zoneId);
      if (!zone) {
        message.error("Zone not found");
        return;
      }

      // Optimistically update UI
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId ? { ...z, isActive: checked } : z
        )
      );

      // Call API to update zone
      await PincodeApi.updateHyperlocalZone({
        id: zoneId,
        isActive: checked,
      });

      // Refresh zones to sync with server
      if (selectedCity) {
        await fetchZonesByCity(selectedCity);
      }

      message.success(`Zone ${checked ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      // Revert UI change on error
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId ? { ...z, isActive: !checked } : z
        )
      );
      console.error("Error updating zone status:", error);
      message.error(
        error.message || "Failed to update zone status. Please try again."
      );
    }
  };

  const handleZoneExpand = (zoneId) => {
    setZones((prev) =>
      prev.map((zone) =>
        zone.id === zoneId ? { ...zone, isExpanded: !zone.isExpanded } : zone
      )
    );
  };

  const handleCreateZone = () => {
    if (!selectedState || !selectedCity) {
      message.warning("Please select state and city first");
      return;
    }
    setIsCreateZoneModalVisible(true);
  };

  const handleZoneCreated = async (zoneData) => {
    try {
      // Validate that at least one pincode is selected
      if (!zoneData.serviceablePincodes || zoneData.serviceablePincodes.length === 0) {
        message.warning("Please select at least one pincode");
        return;
      }

      // Call API to create zone
      await PincodeApi.createHyperlocalZone(zoneData, selectedCity);
      
      message.success("Zone created successfully");
      setIsCreateZoneModalVisible(false);
      
      // Refresh zones list
      if (selectedCity) {
        await fetchZonesByCity(selectedCity);
      }
    } catch (error) {
      console.error("Error creating zone:", error);
      message.error(error.message || "Failed to create zone. Please try again.");
    }
  };

  const handleClearFilters = () => {
    setPincodeFilter("");
    setStateFilter("");
    setCityFilter("");
    setZoneFilter("");
    setStateCodeFilter("");
    setDistrictIpFilter("");
    setPincodeTypeFilter("");
    setServiceabilityTypeFilter("");
    setNewCityFilter("");
    setSearchFilter("");
    setDpIdFilter("");
    setServiceTypeFilter("");
    setIsActiveFilter("");
    setDebouncedPincodeFilter("");
    setDebouncedStateFilter("");
    setDebouncedCityFilter("");
    setDebouncedZoneFilter("");
    setDebouncedStateCodeFilter("");
    setDebouncedDistrictIpFilter("");
    setDebouncedPincodeTypeFilter("");
    setDebouncedServiceabilityTypeFilter("");
    setDebouncedNewCityFilter("");
    setDebouncedSearchFilter("");
    setDebouncedDpIdFilter("");
    setDebouncedServiceTypeFilter("");
    // Clear metadata filters
    setMetadataPincodeFilter("");
    setAreaNameFilter("");
    setAreaTypeFilter("");
    setTransitDaysFilter("");
    setMetadataPincodeTypeFilter("");
    setDebouncedMetadataPincodeFilter("");
    setDebouncedAreaNameFilter("");
    setDebouncedAreaTypeFilter("");
    setDebouncedTransitDaysFilter("");
    setDebouncedMetadataPincodeTypeFilter("");
  };

  // Check if any filter has a value
  const hasActiveFilters =
    pincodeFilter.trim() ||
    stateFilter.trim() ||
    cityFilter.trim() ||
    zoneFilter.trim() ||
    stateCodeFilter.trim() ||
    districtIpFilter.trim() ||
    pincodeTypeFilter.trim() ||
    serviceabilityTypeFilter.trim() ||
    newCityFilter.trim() ||
    searchFilter.trim() ||
    dpIdFilter.trim() ||
    serviceTypeFilter.trim() ||
    isActiveFilter !== "" ||
    metadataPincodeFilter.trim() ||
    areaNameFilter.trim() ||
    areaTypeFilter.trim() ||
    transitDaysFilter.trim() ||
    metadataPincodeTypeFilter.trim();

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const params = {
        business_line: activeTab,
      };
      if (debouncedPincodeFilter.trim()) params.pincode = debouncedPincodeFilter.trim();
      if (debouncedStateFilter.trim()) params.state = debouncedStateFilter.trim();
      if (debouncedCityFilter.trim()) params.city = debouncedCityFilter.trim();
      if (debouncedSearchFilter.trim()) params.search = debouncedSearchFilter.trim();
      if (debouncedDpIdFilter.trim()) params.dp_id = debouncedDpIdFilter.trim();
      if (debouncedServiceTypeFilter.trim()) params.service_type = debouncedServiceTypeFilter.trim();
      if (isActiveFilter !== "") params.is_active = isActiveFilter;
      if (debouncedZoneFilter.trim()) params.zone = debouncedZoneFilter.trim();
      if (activeTab === "courier") {
        if (debouncedStateCodeFilter.trim()) params.state_code = debouncedStateCodeFilter.trim();
        if (debouncedDistrictIpFilter.trim()) params.district_ip = debouncedDistrictIpFilter.trim();
        if (debouncedPincodeTypeFilter.trim()) params.pincode_type = debouncedPincodeTypeFilter.trim();
        if (debouncedServiceabilityTypeFilter.trim()) params.serviceability_type = debouncedServiceabilityTypeFilter.trim();
        if (debouncedNewCityFilter.trim()) params.new_city = debouncedNewCityFilter.trim();
      }
      const response = await PincodeApi.exportPincodeServiceability(params);
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ts = new Date();
      const yy = ts.getFullYear();
      const mm = String(ts.getMonth() + 1).padStart(2, "0");
      const dd = String(ts.getDate()).padStart(2, "0");
      const hh = String(ts.getHours()).padStart(2, "0");
      const mi = String(ts.getMinutes()).padStart(2, "0");
      const ss = String(ts.getSeconds()).padStart(2, "0");
      const fileName = `pincode_serviceability_${activeTab}_${yy}${mm}${dd}_${hh}${mi}${ss}.csv`;
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Export started");
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  // Function to get filter setters for a given dataIndex
  const getFilterSetters = (dataIndex) => {
    const setters = {
      pincode: [setPincodeFilter, setDebouncedPincodeFilter],
      state: [setStateFilter, setDebouncedStateFilter],
      city: [setCityFilter, setDebouncedCityFilter],
      zone: [setZoneFilter, setDebouncedZoneFilter],
      state_code: [setStateCodeFilter, setDebouncedStateCodeFilter],
      district_ip: [setDistrictIpFilter, setDebouncedDistrictIpFilter],
      pincode_type: [setPincodeTypeFilter, setDebouncedPincodeTypeFilter],
      serviceability_type: [setServiceabilityTypeFilter, setDebouncedServiceabilityTypeFilter],
      new_city: [setNewCityFilter, setDebouncedNewCityFilter],
      search: [setSearchFilter, setDebouncedSearchFilter],
      dp_id: [setDpIdFilter, setDebouncedDpIdFilter],
      service_type: [setServiceTypeFilter, setDebouncedServiceTypeFilter],
      // Metadata filters
      metadata_pincode: [setMetadataPincodeFilter, setDebouncedMetadataPincodeFilter],
      area_name: [setAreaNameFilter, setDebouncedAreaNameFilter],
      area_type: [setAreaTypeFilter, setDebouncedAreaTypeFilter],
      transit_days_for_delivery: [setTransitDaysFilter, setDebouncedTransitDaysFilter],
      metadata_pincode_type: [setMetadataPincodeTypeFilter, setDebouncedMetadataPincodeTypeFilter],
    };
    return setters[dataIndex];
  };

  // Function to get current filter value for a given dataIndex
  const getFilterValue = (dataIndex) => {
    const values = {
      pincode: pincodeFilter,
      state: stateFilter,
      city: cityFilter,
      zone: zoneFilter,
      state_code: stateCodeFilter,
      district_ip: districtIpFilter,
      pincode_type: pincodeTypeFilter,
      serviceability_type: serviceabilityTypeFilter,
      new_city: newCityFilter,
      search: searchFilter,
      dp_id: dpIdFilter,
      service_type: serviceTypeFilter,
      // Metadata filters
      metadata_pincode: metadataPincodeFilter,
      area_name: areaNameFilter,
      area_type: areaTypeFilter,
      transit_days_for_delivery: transitDaysFilter,
      metadata_pincode_type: metadataPincodeTypeFilter,
    };
    return values[dataIndex] || "";
  };

  // Function to create column search props (similar to PRSAnalytics)
  const getColumnSearchProps = (dataIndex, placeholder) => {
    const currentFilterValue = getFilterValue(dataIndex);

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => {
        const updateFilter = (value) => {
          const setters = getFilterSetters(dataIndex);
          if (setters) {
            const [setFilter, setDebouncedFilter] = setters;
            // Set both immediately to trigger API call without waiting for debounce
            setFilter(value);
            setDebouncedFilter(value);
            // Reset page to 1 when filter changes
            setCurrentPage(1);
            // Mark that we need to force API call
            forceFetchRef.current = true;
          }
        };

        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search ${placeholder}...`}
              value={selectedKeys[0]}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedKeys(value ? [value] : []);
                // Don't update filter state here - only update on Enter or Search button click
              }}
              onPressEnter={() => {
                const value = selectedKeys[0] || "";
                updateFilter(value);
                confirm();
              }}
              style={{
                marginBottom: 8,
                display: "block",
                borderRadius: "6px",
              }}
              allowClear={false}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  const value = selectedKeys[0] || "";
                  updateFilter(value);
                  confirm();
                }}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  clearFilters();
                  setSelectedKeys([]);
                  updateFilter("");
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: currentFilterValue ? [currentFilterValue] : null,
    };
  };

  const handleServiceToggle = async (record, service) => {
    // Only allow toggle when edit mode is enabled
    if (!editMode) {
      return;
    }

    try {
      // Optimistically update UI
      setPincodeData((prev) =>
        prev.map((item) =>
          item.pincode === record.pincode
            ? {
              ...item,
              services: item.services.map((s) =>
                s.id === service.id ? { ...s, is_active: !s.is_active } : s
              ),
            }
            : item
        )
      );

      // Call API to toggle service
      const toggleData = [
        {
          pincode: record.pincode,
          service_id: service.id,
        },
      ];

      const response = await PincodeApi.togglePincodeService(toggleData);

      // Check if toggle was successful
      const updatedService = response.updated?.[0];
      if (updatedService?.message === "Not found") {
        throw new Error("Service not found for this pincode");
      }

      // Sync with server response to ensure consistency
      if (updatedService?.is_active !== undefined) {
        setPincodeData((prev) =>
          prev.map((item) =>
            item.pincode === record.pincode
              ? {
                ...item,
                services: item.services.map((s) =>
                  s.id === service.id
                    ? { ...s, is_active: updatedService.is_active }
                    : s
                ),
              }
              : item
          )
        );
      }

      // Refresh data to sync with server
      await fetchPincodeData();

      message.success(
        `Service ${service.slug.toUpperCase()} ${
          updatedService?.is_active ? "activated" : "deactivated"
        } for pincode ${record.pincode}`
      );
    } catch (error) {
      // Revert UI change on error
      setPincodeData((prev) =>
        prev.map((item) =>
          item.pincode === record.pincode
            ? {
              ...item,
              services: item.services.map((s) =>
                s.id === service.id ? { ...s, is_active: !s.is_active } : s
              ),
            }
            : item
        )
      );
      console.error("Error toggling service:", error);
      message.error(
        error.message || "Failed to toggle service. Please try again."
      );
    }
  };

  const renderServiceTags = (services, record) => {
    const serviceList = Array.isArray(services) ? services : [];

    return (
      <div className="service-tags-container">
        {serviceList.length === 0 ? (
          <span className="no-services">No services</span>
        ) : (
          serviceList.map((service) => {
            // Use the is_active flag from the API response
            const isActive = service.is_active === true;
            return (
              <Tag
                key={service.id}
                className={`service-tag ${
                  isActive ? "service-tag-active" : "service-tag-inactive"
                } ${editMode ? "service-tag-clickable" : ""}`}
                onClick={() => editMode && handleServiceToggle(record, service)}
                style={{
                  cursor: editMode ? "pointer" : "not-allowed",
                  opacity: isActive ? 1 : 0.5,
                }}
                title={
                  editMode
                    ? `Click to ${isActive ? "deactivate" : "activate"} ${
                      service.name
                    }`
                    : "Enable Edit Mode to make changes"
                }
              >
                {service.slug.toUpperCase()}
              </Tag>
            );
          })
        )}
      </div>
    );
  };

  // Helper function to render null-safe values
  const renderNullableValue = (value) => {
    return value ?? "-";
  };

  // Define columns for metadata tab
  const getMetadataColumns = () => {
    return [
      {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
        width: 100,
        ...getColumnSearchProps("metadata_pincode", "Pincode"),
      },
      // {
      //   title: "Area Name",
      //   dataIndex: "area_name",
      //   key: "area_name",
      //   width: 200,
      //   ...getColumnSearchProps("area_name", "Area Name"),
      // },
      {
        title: "Area Type",
        dataIndex: "area_type",
        key: "area_type",
        width: 150,
        render: renderNullableValue,
        ...getColumnSearchProps("area_type", "Area Type"),
      },
      {
        title: "Transit Days for Delivery",
        dataIndex: "transit_days_for_delivery",
        key: "transit_days_for_delivery",
        width: 180,
        render: renderNullableValue,
        ...getColumnSearchProps("transit_days_for_delivery", "Transit Days"),
      },
      {
        title: "Pincode Type",
        dataIndex: "pincode_type",
        key: "pincode_type",
        width: 150,
        render: renderNullableValue,
        ...getColumnSearchProps("metadata_pincode_type", "Pincode Type"),
      },
    ];
  };

  // Define columns - Action column for both tabs
  const getColumns = () => {
    const baseColumns = [
      {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
        width: 60,
        ...getColumnSearchProps("pincode", "Pincode"),
      },
      {
        title: <span>State</span>,
        dataIndex: "state",
        key: "state",
        width: 120,
        ...getColumnSearchProps("state", "State"),
      },
      {
        title: <span>City</span>,
        dataIndex: "city",
        key: "city",
        width: 150,
        ...getColumnSearchProps("city", "City"),
      },
      {
        title: <span>Zone</span>,
        dataIndex: "zone",
        key: "zone",
        width: 100,
        render: renderNullableValue,
        ...getColumnSearchProps("zone", "Zone"),
      },
      {
        title: <span>Hub Name</span>,
        dataIndex: "hub_name",
        key: "hub_name",
        width: 200,
        render: (value) => value ?? null,
      },
    ];

    // Add courier-specific columns only when activeTab is "courier"
    if (activeTab === "courier") {
      baseColumns.push(
        {
          title: <span>Zone</span>,
          dataIndex: "zone",
          key: "zone",
          width: 100,
          render: renderNullableValue,
          ...getColumnSearchProps("zone", "Zone"),
        },
        {
          title: <span>District IP</span>,
          dataIndex: "district_ip",
          key: "district_ip",
          width: 120,
          render: renderNullableValue,
          ...getColumnSearchProps("district_ip", "District IP"),
        },
        {
          title: <span>State Code</span>,
          dataIndex: "state_code",
          key: "state_code",
          width: 100,
          render: renderNullableValue,
          ...getColumnSearchProps("state_code", "State Code"),
        },
        {
          title: <span>Pincode Type</span>,
          dataIndex: "pincode_type",
          key: "pincode_type",
          width: 120,
          render: renderNullableValue,
          ...getColumnSearchProps("pincode_type", "Pincode Type"),
        },
        {
          title: <span>Serviceability Type</span>,
          dataIndex: "serviceability_type",
          key: "serviceability_type",
          width: 150,
          render: renderNullableValue,
          ...getColumnSearchProps("serviceability_type", "Serviceability Type"),
        }
      );
    }

    // Add Services column for both tabs
    baseColumns.push({
      title: <span>Services</span>,
      dataIndex: "services",
      key: "services",
      width: 400,
      render: (services, record) => renderServiceTags(services, record),
    });

    // Add Action column for both tabs
    baseColumns.push({
      title: <span>Active/Deactivate Pincode</span>,
      key: "action",
      dataIndex: "is_active",
      width: 120,
      align: "center",
      render: (is_active, record) => (
        <Space size={12}>
          <Switch
            checked={is_active}
            onChange={(checked) => handleStatusToggle(record, checked)}
            className="status-switch"
            size="small"
            disabled={!editMode}
          />
        </Space>
      ),
    });

    return baseColumns;
  };

  // const rowSelection = {
  //   selectedRowKeys,
  //   onChange: setSelectedRowKeys,
  //   onSelectAll: (selected) => {
  //     if (selected) {
  //       setSelectedRowKeys(pincodeData.map(item => item.key));
  //     } else {
  //       setSelectedRowKeys([]);
  //     }
  //   },
  // };

  // Since filtering is done on the server side, we use the data directly
  const paginatedData = activeTab === "metadata" ? metadataData : pincodeData;
  const tableColumns = activeTab === "metadata" ? getMetadataColumns() : getColumns();

  return (
    <div className="pincode-management">
      {/* Header */}
      <div className="pincode-header">
        <div className="header-left">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            className="back-btn"
          />
          <h1 className="page-title">Pincode Management</h1>
        </div>
      </div>

      {/* Tabs and Actions */}
      <div className="pincode-controls">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="pincode-tabs"
          moreIcon={null}
          tabBarGutter={32}
        >
          <TabPane tab="Ecomm" key="ecomm" />
          <TabPane tab="Courier" key="courier" />
          <TabPane tab="Hyperlocal" key="hyperlocal" />
          <TabPane tab="Pincode metadata" key="metadata" />
        </Tabs>

        <div className="controls-right">
          <Button
            type="default"
            icon={<CloseCircleOutlined />}
            onClick={handleClearFilters}
            className="clear-filters-btn"
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </Button>
          {activeTab !== "metadata" && activeTab !== "hyperlocal" && (
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              className="clear-filters-btn"
              loading={exportLoading}
            >
              Export
            </Button>
          )}
          {activeTab !== "metadata" && activeTab !== "hyperlocal" && (
            <Button
              type={editMode ? "default" : "primary"}
              icon={editMode ? <CloseOutlined /> : <EditOutlined />}
              onClick={() => setEditMode(!editMode)}
              className={`edit-mode-btn ${editMode ? "edit-mode-active" : ""}`}
              danger={editMode}
            >
              {editMode ? "Done" : "Edit"}
            </Button>
          )}
          {activeTab !== "metadata" && activeTab !== "hyperlocal" && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddPincode}
              className="add-pincode-btn"
            >
              ADD PINCODE
            </Button>
          )}
        </div>
      </div>

      {/* Edit Mode Alert */}
      {editMode && activeTab !== "hyperlocal" && (
        <Alert
          message={
            <span>
              <EditOutlined style={{ marginRight: 8 }} />
              <strong>Edit Active:</strong> You can now edit services and
              pincode status. Click &quot;Done&quot; when finished.
            </span>
          }
          type="warning"
          closable={false}
          className="edit-mode-alert"
          style={{ marginBottom: 16 }}
          showIcon={false}
        />
      )}

      {/* Hyperlocal View */}
      {activeTab === "hyperlocal" ? (
        <div className="hyperlocal-container">
          {/* Hyperlocal Filters */}
          <div className="hyperlocal-filters">
            <select
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
              className="hyperlocal-select"
              disabled={loadingStates}
            >
              <option value="">
                {loadingStates ? "Loading states..." : "Select State*"}
              </option>
              {Array.isArray(states) && states.map((state, index) => {
                const stateName = typeof state === "string" ? state : state.name || state.stateName;
                const stateValue = typeof state === "string" ? state : state.name || state.stateName;
                return (
                  <option key={`state-${index}`} value={stateValue}>
                    {stateName}
                  </option>
                );
              })}
            </select>

            <select
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
              className="hyperlocal-select"
              disabled={!selectedState || loadingCities}
            >
              <option value="">
                {loadingCities ? "Loading cities..." : "City *"}
              </option>
              {Array.isArray(cities) && cities.map((city, index) => {
                const cityName = typeof city === "string" ? city : city.name || city.cityName;
                const cityValue = typeof city === "string" ? city : city.name || city.cityName;
                return (
                  <option key={`city-${index}`} value={cityValue}>
                    {cityName}
                  </option>
                );
              })}
            </select>

            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClearHyperlocalFilters}
              className="hyperlocal-clear-btn"
              size="large"
            />

            <Button
              type="primary"
              onClick={handleCreateZone}
              className="hyperlocal-create-btn"
              size="large"
              disabled={!selectedState || !selectedCity}
            >
              Create Zone
            </Button>
          </div>

          {/* Zone Cards */}
          <div className="zone-cards-container">
            {loadingZones ? (
              <div className="zone-loading">Loading zones...</div>
            ) : zones.length === 0 ? (
              <div className="zone-empty">
                {selectedCity ? "No zones found for this city" : "Select a state and city to view zones"}
              </div>
            ) : (
              zones.map((zone) => (
                <div key={zone.id} className="zone-card">
                  <div className="zone-card-header">
                    <span className="zone-name">{zone.name}</span>
                    <div className="zone-actions">
                      <Switch
                        checked={zone.isActive}
                        onChange={(checked) => handleZoneToggle(zone.id, checked)}
                        size="default"
                      />
                      <DownOutlined
                        className={`zone-expand-icon ${
                          zone.isExpanded ? "expanded" : ""
                        }`}
                        onClick={() => handleZoneExpand(zone.id)}
                      />
                    </div>
                  </div>
                  {zone.isExpanded && (
                    <div className="zone-card-content">
                      <div className="zone-info">
                        <div className="zone-info-item">
                          <strong>State:</strong> <span>{zone.state}</span>
                        </div>
                        <div className="zone-info-item">
                          <strong>Order Process Type:</strong> <span>{zone.orderProcessType}</span>
                        </div>
                        <div className="zone-info-item">
                          <strong>Total Pincodes:</strong> <span>{zone.serviceablePincodes?.length || 0}</span>
                        </div>
                      </div>
                      
                      {zone.serviceablePincodes && zone.serviceablePincodes.length > 0 && (
                        <div className="pincodes-section">
                          <h4 className="pincodes-title">Serviceable Pincodes</h4>
                          <div className="pincode-cards-grid">
                            {zone.serviceablePincodes.map((pincodeItem, idx) => (
                              <div key={`pincode-${idx}`} className="pincode-mini-card">
                                <div className="pincode-header">
                                  <span className="pincode-number">{pincodeItem.pincode}</span>
                                </div>
                                {pincodeItem.area && pincodeItem.area.length > 0 && (
                                  <div className="pincode-areas">
                                    {pincodeItem.area.map((areaName, areaIdx) => (
                                      <span key={`area-${idx}-${areaIdx}`} className="area-tag">
                                        {areaName}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {(!pincodeItem.area || pincodeItem.area.length === 0) && (
                                  <div className="pincode-no-areas">No specific areas</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="pincode-table-container" id="pincode-table-container">
            <Table
              columns={tableColumns}
              dataSource={paginatedData}
              pagination={false}
              loading={loading}
              className="pincode-table"
              scroll={{ 
                x: activeTab === "courier" ? 1600 : activeTab === "metadata" ? 1400 : 1200 
              }}
              getPopupContainer={(trigger) => {
                return trigger.closest(".pincode-table-container") || document.body;
              }}
            />
          </div>

          {/* Pagination */}
          <div className="pincode-pagination">
            <div className="pagination-info">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, totalRecords)} of{" "}
              {totalRecords.toLocaleString()} results
            </div>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              onChange={setCurrentPage}
              showSizeChanger={false}
              className="pagination-controls"
              itemRender={(page, type, originalElement) => {
                if (type === "prev") {
                  return <span>Previous</span>;
                }
                if (type === "next") {
                  return <span>Next</span>;
                }
                return originalElement;
              }}
            />
          </div>
        </>
      )}

      {/* Modals and Drawers */}
      <AddPincodeModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        mode={modalMode}
        pincodeData={selectedPincode}
        activeTab={activeTab}
        onSuccess={() => {
          setIsAddModalVisible(false);
          if (activeTab === "metadata") {
            fetchPincodeMetadata();
          } else {
            fetchPincodeData(); // Refresh data
          }
        }}
      />

      <PincodeDetailsDrawer
        visible={isDetailsDrawerVisible}
        onClose={() => setIsDetailsDrawerVisible(false)}
        pincodeData={selectedPincode}
        onEdit={handleEditPincode}
      />

      <CreateZoneModal
        visible={isCreateZoneModalVisible}
        onCancel={() => setIsCreateZoneModalVisible(false)}
        selectedState={selectedState}
        selectedCity={selectedCity}
        onSuccess={handleZoneCreated}
      />
    </div>
  );
};

export default PincodeManagement;
